import React from 'react'
import { connect, useSelector } from 'react-redux'

import { selectIsShowcaseMultiScreen } from '@store/actionSlices/multiScreen'
import {
  InteractivePlanType,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import DataHandler from '@components/data-handler/v2'
import { Status } from '@components/data-handler/v2/data-handler'
import usePropertyControls from '@components/properties-view/v2/utils/use-property-controls'
import VideoPlaybackActions, {
  ActionType,
} from '@components/video-playback-actions'
import VideoPlayerControl from '@components/video-player'

import { DEFAULT_GALLERY_SETTINGS } from '@pages/properties-view/house-and-land/v2/constants'

import {
  LotInterface,
  selectPrecinctListFromResult,
  useGetPrecinctListQuery,
} from '@api/houseAndLand'
import {
  selectFromResult as selectStageFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'
import {
  selectFromResult as selectFromPanoramicResult,
  useGetPanoramicQuery,
} from '@api/panoramic'

import useCheckMountedStatus from '@utilities/check-mounted-status'
import { getTabIndexByKey, getTabKeyByIndexBy } from '@utilities/floor-tab'
import useTabVisibility from '@utilities/tab-visibility'
import VideoPlayerFirebaseService from '@utilities/video-player-firebase-service'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import VideoPlayer from '@adUtilities/components/video-player'
import { VideoPlayerRef } from '@adUtilities/components/video-player/video-player'
import {
  CarouselControlDirection,
  CarouselControlType,
} from '@adUtilities/constants/carousel'
import { FileType, PlayerState } from '@adUtilities/constants/common'
import { GallerySettings } from '@adUtilities/types/carousel'

import GeneralExternalLinkFrame from '../../../common/GeneralExternalLinkFrame'
import PropertiesFooterButtons from '../../../common/PropertiesFooterButtons'
import useGetFloorPlateGalleries from '../../../hooks/useGetFloorPlateGalleries'
import useGetGeneralExternalLinks from '../../../hooks/useGetGeneralExternalLinks'
import Controls from './controls'
import StageCanvas from './stage-canvas'
import StageSkeleton from './stage-skeleton'

interface ComponentProps {
  originalLots: Array<LotInterface>
  filteredLots: Array<LotInterface>
  getVideoPlayerState: (arg: boolean) => void
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  activePrecinctId: string
  activeStageId: string
  activeLotId: string
  fullScreenToggle: boolean
  setFullScreenToggle: (arg: boolean) => void
}

function StageView({
  originalLots,
  filteredLots,
  getVideoPlayerState,
  projectIdentity,
  session,
  activePrecinctId,
  activeStageId,
  activeLotId,
  fullScreenToggle,
  setFullScreenToggle,
}: ComponentProps) {
  const videoPlayerRef = React.useRef<VideoPlayerRef>(null)
  const connectedRef = React.useRef(false)

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const videoPlayerFirebaseService = VideoPlayerFirebaseService({
    galleryName: 'floorGallery',
    projectIdentity,
  })

  const isShowcaseMultiScreen = useSelector(selectIsShowcaseMultiScreen)

  const [isConnected, setIsConnected] = React.useState(false)

  const [isBuffering, setBufferState] = React.useState(false)
  const [scrubbarTimerState, setScrubbarTimerState] = React.useState(false)
  const [playerControlState, setPlayerControlState] =
    React.useState<PlayerState>(PlayerState.Stop)
  const [playerState, setPlayerState] = React.useState<PlayerState>(
    PlayerState.Stop
  )
  const [isMuted, setMuteState] = React.useState(true)
  const [volume, setVolume] = React.useState<number>(0)
  const [videoLengthInSeconds, setVideoLengthInSeconds] = React.useState(0)
  const [currentVideoPositionInSeconds, setCurrentVideoPositionInSeconds] =
    React.useState(0)
  const [isSeeking, setSeekingState] = React.useState(false)
  const [isExternalLinkFrameOpen, setIsExternalLinkFrameOpen] =
    React.useState(false)

  const pinnedGalleryItemIndex =
    session?.floorGallery?.galleryControlV2?.pinnedItemIndex ?? null

  const gallerySettings: GallerySettings = React.useMemo(
    () => ({
      autoPlayIntervalInSeconds:
        projectIdentity?.gallerySettings?.intervalInSeconds ||
        DEFAULT_GALLERY_SETTINGS.autoPlayIntervalInSeconds,
      effectType:
        projectIdentity?.gallerySettings?.type ||
        DEFAULT_GALLERY_SETTINGS.effectType,
    }),
    [projectIdentity?.gallerySettings]
  )

  const precinctsPayload = useGetPrecinctListQuery(
    { projectName: projectIdentity.projectName },
    {
      selectFromResult: selectPrecinctListFromResult,
    }
  )

  const precinctList = precinctsPayload?.precinctList

  const stagePlanMapKey = React.useMemo(
    () =>
      precinctList?.length > 1
        ? `${activePrecinctId}-${activeStageId}`
        : activeStageId,
    [precinctList, activePrecinctId, activeStageId]
  )

  const stagePayload = useGetInteractivePlanQuery(
    {
      projectName: projectIdentity.projectName,
      type: InteractivePlanType.Stages,
      slug: stagePlanMapKey,
    },
    { selectFromResult: selectStageFromResult, skip: !activeStageId }
  )

  const stagePlan = stagePayload?.maps.stages

  const { slides } = useGetFloorPlateGalleries()
  const { generalExternalLink } = useGetGeneralExternalLinks()

  const panoramicPayload = useGetPanoramicQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromPanoramicResult }
  )

  const panoramic = panoramicPayload.panoramicData

  const [activeTab, setActiveTab] = React.useState('floor')
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0)

  const hasVideoItems: boolean = React.useMemo(
    () => slides.filter((item) => item.fileType === FileType.Video).length > 0,
    [slides]
  )

  const videoSource: string = React.useMemo(() => {
    const item = slides.find(
      (slide, index) =>
        slide.fileType === FileType.Video && index === currentSlideIndex
    )

    return item
      ? AssetHandler({
          url: item.videoSource || '',
          type: item.bucketType,
          fileType: FileType.Video,
        })
      : ''
  }, [slides, currentSlideIndex])

  const handleChangeCurrentSlideIndex = React.useCallback(
    (index: number) => {
      if (pinnedGalleryItemIndex === null && currentSlideIndex === index) {
        return
      }
      setCurrentSlideIndex(index)
      firebaseControlQuery.update({
        [`floorGallery.galleryControlV2.activeSlideIndex`]: index,
        [`floorGallery.playerControlV2.playerState`]: PlayerState.Stop,
        [`floorGallery.playerControlV2.scrubbarTimerState`]: false,
        [`floorGallery.playerControlV2.videoLengthInSeconds`]: 0,
        [`floorGallery.playerControlV2.currentVideoPositionInSeconds`]: 0,
      })
    },
    [currentSlideIndex, pinnedGalleryItemIndex === null]
  )

  const handlePlayerState = React.useCallback(
    (status: boolean) => {
      getVideoPlayerState(status)
      setPlayerState(status ? PlayerState.Play : PlayerState.Stop)
    },
    [getVideoPlayerState]
  )

  const handlePlayClick = React.useCallback(async () => {
    setPlayerControlState(PlayerState.Play)
    setScrubbarTimerState(false)
    await videoPlayerFirebaseService.updatePlayState()
  }, [videoPlayerFirebaseService])

  const handlePauseClick = React.useCallback(async () => {
    setPlayerControlState(PlayerState.Pause)
    setScrubbarTimerState(false)
    await videoPlayerFirebaseService.updatePauseState()
  }, [videoPlayerFirebaseService])

  const handleStopClick = React.useCallback(async () => {
    setScrubbarTimerState(false)
    setPlayerControlState(PlayerState.Stop)
    setVideoLengthInSeconds(0)
    setCurrentVideoPositionInSeconds(0)
    await videoPlayerFirebaseService.updateStopState()
  }, [videoPlayerFirebaseService])

  const handleSeekTo = React.useCallback(
    async (argInSeconds: number) => {
      setScrubbarTimerState(false)
      setSeekingState(true)
      await videoPlayerFirebaseService.updateSeekState(argInSeconds)
    },
    [videoPlayerFirebaseService]
  )

  const handleMuteClick = React.useCallback(
    async (arg: boolean) => {
      setMuteState(arg)
      await videoPlayerFirebaseService.updateMuteState(arg)
    },
    [videoPlayerFirebaseService]
  )

  const handleVolumeDownClick = React.useCallback(async () => {
    if (volume > 0) {
      const newVolume = volume - 1

      setMuteState(false)
      setVolume(newVolume)
      await videoPlayerFirebaseService.updateVolumeState(newVolume)
    }
  }, [volume, videoPlayerFirebaseService])

  const handleVolumeUpClick = React.useCallback(async () => {
    if (volume < 10) {
      setMuteState(false)
      const newVolume = volume + 1
      setVolume(newVolume)
      await videoPlayerFirebaseService.updateVolumeState(newVolume)
    }
  }, [volume, videoPlayerFirebaseService])

  const handlePlaybackAction = React.useCallback(
    (actionType: ActionType) => {
      if (actionType === 'showcase') {
        setPlayerControlState(PlayerState.Play)
        setScrubbarTimerState(false)
        videoPlayerFirebaseService.updatePlayState()
      }
      if (actionType === 'remote') {
        videoPlayerRef.current?.updatePlayerState(PlayerState.Play)
      }
    },
    [videoPlayerFirebaseService]
  )

  const requestForVideoSync = React.useCallback(async () => {
    await videoPlayerFirebaseService.updateSyncState()
  }, [videoPlayerFirebaseService])

  useTabVisibility(
    React.useCallback(async () => {
      if (playerControlState === PlayerState.Play) {
        requestForVideoSync()
      }
    }, [playerControlState])
  )

  const isMounted = useCheckMountedStatus({
    disable: !session?.connected,
  })

  React.useEffect(() => {
    if (hasVideoItems && playerControlState === PlayerState.Play) {
      requestForVideoSync()
    }
  }, [hasVideoItems, playerControlState])

  React.useEffect(() => {
    if (!session) {
      return
    }
    const {
      connected,
      floorGallery: {
        galleryControlV2: {
          activeSlideIndex: activeSlideIndexFirebase,
          activeTabIndex: activeTabIndexFirebase,
        },
        playerControlV2: {
          playerState: playerStateFirebase,
          videoLengthInSeconds: videoLengthInSecondsFirebase,
          currentVideoPositionInSeconds: currentVideoPositionInSecondsFirebase,
          volume: volumeFirebase,
          scrubbarTimerState: scrubbarTimerStateFirebase,
          seekToValueInSeconds: seekToValueInSecondsFirebase,
          isBuffering: isBufferingFirebase,
          muted: mutedFirebase,
        },
      },
    } = session
    connectedRef.current = connected
    setIsConnected(connected)
    setCurrentSlideIndex(activeSlideIndexFirebase)
    setActiveTab(getTabKeyByIndexBy(activeTabIndexFirebase || 0))

    setPlayerControlState(playerStateFirebase)
    setVideoLengthInSeconds(videoLengthInSecondsFirebase)
    setCurrentVideoPositionInSeconds(currentVideoPositionInSecondsFirebase)
    setVolume(volumeFirebase)
    setScrubbarTimerState(scrubbarTimerStateFirebase)
    setBufferState(isBufferingFirebase)
    setSeekingState(seekToValueInSecondsFirebase !== null)
    setMuteState(mutedFirebase)
  }, [session?.connected, session?.floorGallery])

  React.useEffect(() => {
    if (!isConnected) {
      return
    }
    firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.activeSlideIndex`]: 0,
      [`floorGallery.galleryControlV2.activeTabIndex`]:
        getTabIndexByKey('floor'),
      [`floorGallery.playerControlV2.playerState`]: PlayerState.Stop,
      [`floorGallery.playerControlV2.scrubbarTimerState`]: false,
      [`floorGallery.playerControlV2.videoLengthInSeconds`]: 0,
      [`floorGallery.playerControlV2.currentVideoPositionInSeconds`]: 0,
    })
  }, [activePrecinctId, activeStageId, activeLotId])

  const shouldShowVideoPlayerActionButtons = React.useMemo(() => {
    const isPinned = pinnedGalleryItemIndex !== null
    const isStopped =
      playerControlState === PlayerState.Stop &&
      playerState === PlayerState.Stop

    if (!isPinned) {
      return hasVideoItems && isStopped
    }

    if (pinnedGalleryItemIndex === currentSlideIndex) {
      return hasVideoItems && isStopped
    }

    return false
  }, [
    hasVideoItems,
    playerState,
    playerControlState,
    pinnedGalleryItemIndex,
    currentSlideIndex,
  ])

  const resetPin = React.useCallback(() => {
    firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.pinnedItemIndex`]: null,
    })
  }, [])

  React.useEffect(
    () => () => {
      resetPin()
    },
    []
  )

  React.useEffect(() => {
    if (!isMounted) {
      return
    }
    resetPin()
  }, [activeStageId])

  const {
    forcedFullscreenIsActive,
    showControlsInForcedFullscreen,
    reset: resetControls,
  } = usePropertyControls({
    fullscreenConfig: projectIdentity.fullScreen,
    isFullscreenActive: fullScreenToggle,
    disableToggle:
      playerControlState !== PlayerState.Stop ||
      playerState !== PlayerState.Stop,
  })

  const showPropertyControls = React.useMemo(() => {
    if (activeTab !== 'gallery') {
      return false
    }
    if (forcedFullscreenIsActive) {
      return showControlsInForcedFullscreen
    }
    return true
  }, [activeTab, forcedFullscreenIsActive, showControlsInForcedFullscreen])

  const handleFloorGalleryTabClick = async (tabKey: string) => {
    resetControls()
    setActiveTab(tabKey)
    await firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.activeSlideIndex`]: 0,
      [`floorGallery.galleryControlV2.activeTabIndex`]:
        getTabIndexByKey(tabKey),
    })
  }

  const handleBackActionClickOnGalleryTab = React.useCallback(() => {
    firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.pinnedItemIndex`]: null,
    })
    handleFloorGalleryTabClick('floor')
  }, [])

  const dataHandlerStatus = React.useMemo<string>(() => {
    if (precinctsPayload.status !== Status.FULFILLED) {
      return precinctsPayload.status
    }
    return stagePayload.status
  }, [precinctsPayload.status, stagePayload.status])

  if (isExternalLinkFrameOpen && generalExternalLink) {
    return (
      <GeneralExternalLinkFrame
        onCloseGeneralExternalLinkFrame={() =>
          setIsExternalLinkFrameOpen(false)
        }
        generalExternalLink={generalExternalLink}
        isFullscreen={fullScreenToggle}
      />
    )
  }

  return (
    <DataHandler
      payload={{
        ...stagePayload,
        status: dataHandlerStatus,
        data: Object.entries(stagePlan?.[stagePlanMapKey] || {}),
      }}
      skeletonFrame={<StageSkeleton />}
    >
      <div className="relative h-full w-full">
        <div className="absolute left-6 top-4 z-10">
          <Controls
            fullScreenToggle={fullScreenToggle}
            setFullScreenToggle={setFullScreenToggle}
            isVideoPlaying={
              playerControlState !== PlayerState.Stop ||
              playerState !== PlayerState.Stop
            }
            isPinned={pinnedGalleryItemIndex !== null}
            hidePropertyControls={!showPropertyControls}
            currentSlideIndex={currentSlideIndex}
            onBackActionOverride={
              activeTab === 'gallery'
                ? handleBackActionClickOnGalleryTab
                : undefined
            }
            forceShowBackAction={activeTab === 'gallery'}
          />
        </div>
        {activeTab === 'gallery' && slides.length > 0 ? (
          <div className="absolute inset-0">
            <CarouselProvider
              slides={slides}
              activeSlideIndex={currentSlideIndex}
              showControl={
                playerControlState === PlayerState.Stop &&
                playerState === PlayerState.Stop
              }
              controlType={CarouselControlType.Thumbnail}
              getCurrentSlideIndex={handleChangeCurrentSlideIndex}
              mousewheel={false}
              gallerySettings={gallerySettings}
              allowSlideSwiping={
                playerControlState === PlayerState.Stop &&
                playerState === PlayerState.Stop &&
                pinnedGalleryItemIndex === null
              }
              controlDirection={CarouselControlDirection.Vertical}
              pinnedGalleryItemIndex={pinnedGalleryItemIndex}
              toggleControls={
                forcedFullscreenIsActive
                  ? showControlsInForcedFullscreen
                  : undefined
              }
            />

            {shouldShowVideoPlayerActionButtons ? (
              <div
                className={`absolute inset-0 transition-all duration-300 ${videoSource ? 'visible' : 'hidden'}`}
              >
                <div className="flex h-full w-full items-center justify-center">
                  <VideoPlaybackActions onPlay={handlePlaybackAction} />
                </div>
              </div>
            ) : null}

            {hasVideoItems ? (
              <div
                className={`absolute bottom-0 z-40 w-full px-2 pb-4 transition-all duration-300 ${
                  playerControlState !== PlayerState.Stop
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none fixed translate-y-full opacity-0'
                } ${isShowcaseMultiScreen && 'flex justify-center'}`}
              >
                <VideoPlayerControl
                  scrubbarTimerState={scrubbarTimerState}
                  isBuffering={isBuffering}
                  isMuted={isMuted}
                  volume={volume}
                  isLooping={false}
                  playerState={playerControlState}
                  videoLengthInSeconds={videoLengthInSeconds}
                  currentVideoPositionInSeconds={currentVideoPositionInSeconds}
                  isSeeking={isSeeking}
                  handleStopClick={handleStopClick}
                  handlePauseClick={handlePauseClick}
                  handlePlayClick={handlePlayClick}
                  handleMuteClick={handleMuteClick}
                  handleVolumeUpClick={handleVolumeUpClick}
                  handleVolumeDownClick={handleVolumeDownClick}
                  handleSeekTo={handleSeekTo}
                  showLoopOption={false}
                  isShowcaseMultiScreen={isShowcaseMultiScreen}
                />
              </div>
            ) : null}

            {hasVideoItems ? (
              <div
                className={`absolute inset-0 ${
                  videoSource && playerState !== PlayerState.Stop
                    ? 'visible'
                    : 'invisible'
                }`}
              >
                <div className="flex h-full w-full items-center justify-center">
                  <VideoPlayer
                    ref={videoPlayerRef}
                    sources={[videoSource]}
                    activeIndex={0}
                    getPlayerState={handlePlayerState}
                    showControls
                    showLoopOption={false}
                    hidePlayButton
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <StageCanvas
            stagePlan={stagePlan?.[stagePlanMapKey]}
            originalLots={originalLots}
            filteredLots={filteredLots}
            session={session}
            fullScreenToggle={fullScreenToggle}
          />
        )}

        <PropertiesFooterButtons
          panoramic={panoramic}
          activeTab={activeTab}
          isVideoPlaying={
            playerControlState !== PlayerState.Stop ||
            playerState !== PlayerState.Stop
          }
          shouldShowGalleryTab={slides.length > 0}
          onTabChange={(tab: string) => {
            handleFloorGalleryTabClick(tab)
          }}
          hasGeneralExternalLink={Boolean(generalExternalLink)}
          onOpenGeneralExternalLinkFrame={() =>
            setIsExternalLinkFrameOpen(true)
          }
        />
      </div>
    </DataHandler>
  )
}

const mapStateToProps = ({
  projectIdentity,
  firestore: { session },
}: RootStateTypeExtra) => ({
  projectIdentity,
  session,
  activePrecinctId: session?.houseAndLand?.activePrecinctId || '',
  activeStageId: session?.houseAndLand?.activeStageId || '',
  activeLotId: session?.houseAndLand?.activeLotId || '',
})

export default connect(mapStateToProps)(StageView)
